import { useLocation } from '@gatsbyjs/reach-router'
import classnames from 'classnames'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'

import useSiteMetadata from '../../../hooks/useSiteMetadata'
import useSiteSettings from '../../../hooks/useSiteSettings'
import useUserCategory from '../../../hooks/useUserCategory'
import { eftAndTaxTitle } from '../../../lib/practitioner-settings/customerForms'
import { usePractitionerSettingsEager } from '../../../lib/practitioner-settings/hooks/usePractitionerSettingsEager'
import useRewardsOrders from '../../../lib/rewards/hooks/useRewardsOrders'
import { langCodes } from '../../../utils/siteSettings'
import Link, { LinkProps } from '../../global/Link'
import {
  AccountSettingsIcon,
  AutoShipIcon,
  BillingShippingIcon,
  DashboardIcon,
  GenomicsSpotlightIcon,
  PacketsIcon,
  PurchasesIcon,
  RewardsOrderHistoryIcon,
  VirtualDispensaryIcon,
  WellWorldIcon,
} from './icons'

export interface NavProps {
  className?: string
  /** Callback to close mobile menu on navigation */
  closeMenu?: () => void
}

interface GroupHeaderProps {
  activeClassName: string
  className: string
  pathPrefixes: string[]
}

/**
 * Wrap Link component to close the mobile menu on navigation
 */
const NavLink: FC<LinkProps & { closeMenu: (() => void) | undefined }> = ({
  children,
  onClick,
  closeMenu,
  ...otherProps
}) => {
  // if `closeMenu` provided, wrap `onClick` (if any) to close mobile menu
  const wrappedOnClick: typeof onClick = closeMenu
    ? (e) => {
        closeMenu()
        if (onClick) {
          onClick(e)
        }
      }
    : onClick
  return (
    <Link onClick={wrappedOnClick} {...otherProps}>
      {children}
    </Link>
  )
}

const GroupHeader: React.FC<GroupHeaderProps> = ({
  activeClassName,
  children,
  className,
  pathPrefixes,
}) => {
  const location = useLocation()
  const currentPath = location ? location.pathname : ''
  // since some grouped nav entries are not hierarchical, determine whether group is active
  // if the current path starts with any of the path prefixes
  const isAnyPrefixActive = useMemo(
    () => pathPrefixes.some((prefix) => currentPath.startsWith(prefix)),
    [currentPath, pathPrefixes],
  )
  return (
    <div className={classnames(className, { [activeClassName]: isAnyPrefixActive })}>
      {children}
    </div>
  )
}

const getPropsForNestedNavLink = ({
  isCurrent,
  isPartiallyCurrent,
}: {
  isCurrent: boolean
  isPartiallyCurrent: boolean
}) => ({
  className: classnames('nav-link', 'nested-nav-link', {
    activeAccountTab: isCurrent || isPartiallyCurrent,
  }),
})

const Nav: FC<NavProps> = ({ className, closeMenu }) => {
  const { sources: rewardsSources, rewardsHistorySources, showRewardsOrders } = useRewardsOrders()
  const siteSettings = useSiteSettings()
  const { userCategory } = useUserCategory()
  const { data: settings } = usePractitionerSettingsEager()
  const {
    featureFlags,
    legalAgreements,
    manageWellWorldEntryPoint,
    manageGenomicsSpotlightEntryPoint,
  } = useSiteMetadata() ?? {}

  const {
    manageDispensary: showDispensary,
    manageDispensingOptions: showDispensingOptions,
    manageRebateProgram: showRebateProgram,
    manageWellWorld: showWellWorld,
    manageGenomicsSpotlight: showGenomicsSpotlight,
    manageInboundPatientReferral: showContactInformation,
    privateLabelCustomPackets: showPrivateLabelCustomPackets,
  } = featureFlags ?? {}

  const isPatient = userCategory === 'patient'
  const isPractitioner = userCategory === 'practitioner'
  const isUSPractitioner = siteSettings.lang === langCodes.en_US && isPractitioner
  const isCAPractitioner = siteSettings.lang === langCodes.en_CA && isPractitioner

  const eftAndTaxLink = useMemo(() => {
    const provisioningPending = settings?.practitioner?.provisioningPending ?? []
    const hideAccountDocuments =
      provisioningPending.length > 0 && provisioningPending.includes('VENDOR')
    // determine title from whether form link is populated
    const eftAndTaxText =
      isUSPractitioner && !hideAccountDocuments
        ? 'Account Documents'
        : isCAPractitioner
          ? eftAndTaxTitle(siteSettings)
          : undefined
    return eftAndTaxText ? (
      <NavLink
        closeMenu={closeMenu}
        to={`/account/settings/${isUSPractitioner ? 'documents' : 'eft-and-tax'}`}
        getProps={getPropsForNestedNavLink}
      >
        {eftAndTaxText}
      </NavLink>
    ) : undefined
  }, [
    closeMenu,
    isCAPractitioner,
    isUSPractitioner,
    settings?.practitioner?.provisioningPending,
    siteSettings,
  ])

  return (
    <nav className={`nav nav-pills ${className}`}>
      <div className="nav-group">
        <NavLink
          closeMenu={closeMenu}
          to="/account"
          getProps={({ isCurrent, location }) => ({
            className: classnames('nav-link', 'top-level-nav-item', {
              activeAccountTab: isCurrent || (location || {}).pathname === '/account/',
            }),
          })}
        >
          <DashboardIcon />
          Account Dashboard
        </NavLink>
      </div>
      {isPractitioner && showWellWorld && manageWellWorldEntryPoint && (
        <div className="nav-group">
          <NavLink
            className="nav-link top-level-nav-item"
            closeMenu={closeMenu}
            target="_blank"
            to={manageWellWorldEntryPoint}
          >
            <WellWorldIcon />
            Well World
          </NavLink>
        </div>
      )}
      {isPractitioner && showGenomicsSpotlight && manageGenomicsSpotlightEntryPoint && (
        <div className="nav-group">
          <NavLink
            className="nav-link top-level-nav-item"
            closeMenu={closeMenu}
            target="_blank"
            to={manageGenomicsSpotlightEntryPoint}
          >
            <GenomicsSpotlightIcon />
            Genomic Spotlight
          </NavLink>
        </div>
      )}
      <div className="nav-group">
        <NavLink
          closeMenu={closeMenu}
          to="/account/autoship"
          getProps={({ isCurrent, isPartiallyCurrent }) => ({
            className: classnames('nav-link', 'top-level-nav-item', {
              activeAccountTab: isCurrent || isPartiallyCurrent,
            }),
          })}
        >
          <AutoShipIcon />
          Auto-Ship
        </NavLink>
      </div>
      {isPatient && (
        <div className="nav-group">
          <NavLink
            closeMenu={closeMenu}
            to="/account/dispensary/contact"
            getProps={({ isCurrent, isPartiallyCurrent }) => ({
              className: classnames('nav-link', 'top-level-nav-item', {
                activeAccountTab: isCurrent || isPartiallyCurrent,
              }),
            })}
          >
            <VirtualDispensaryIcon />
            Practitioner&apos;s Dispensary
          </NavLink>
        </div>
      )}
      {isPractitioner && showDispensary && (
        <div className="nav-group">
          <GroupHeader
            pathPrefixes={['/account/dispensary']}
            className="nav-link top-level-nav-item"
            activeClassName="activeAccountTab"
          >
            <VirtualDispensaryIcon />
            Virtual Dispensary
          </GroupHeader>
          <NavLink
            closeMenu={closeMenu}
            to="/account/dispensary/dashboard"
            getProps={getPropsForNestedNavLink}
          >
            Dispensary Dashboard
          </NavLink>
          {showContactInformation && (
            <NavLink
              closeMenu={closeMenu}
              to="/account/dispensary/contact"
              getProps={getPropsForNestedNavLink}
            >
              Contact Information
            </NavLink>
          )}
          <NavLink
            closeMenu={closeMenu}
            to="/account/dispensary/promos"
            getProps={getPropsForNestedNavLink}
          >
            Dispensary Promotions
          </NavLink>
          <NavLink
            closeMenu={closeMenu}
            to="/account/dispensary/customize/logo"
            getProps={getPropsForNestedNavLink}
          >
            Customize Logo & Bio
          </NavLink>
          <NavLink
            closeMenu={closeMenu}
            to="/account/dispensary/customize/homepage"
            getProps={getPropsForNestedNavLink}
          >
            Customize Home Page
          </NavLink>
          <NavLink
            closeMenu={closeMenu}
            to="/account/dispensary/customize/featured-products"
            getProps={getPropsForNestedNavLink}
          >
            Featured Products
          </NavLink>
        </div>
      )}
      <div className="nav-group">
        <GroupHeader
          pathPrefixes={['/account/orders', '/account/returns']}
          className="nav-link top-level-nav-item"
          activeClassName="activeAccountTab"
        >
          <PurchasesIcon />
          Purchases
        </GroupHeader>
        <NavLink closeMenu={closeMenu} to="/account/orders" getProps={getPropsForNestedNavLink}>
          Order History
        </NavLink>
        <NavLink closeMenu={closeMenu} to="/account/reorder" getProps={getPropsForNestedNavLink}>
          Reorder Items
        </NavLink>
        <NavLink closeMenu={closeMenu} to="/account/returns" getProps={getPropsForNestedNavLink}>
          Returns
        </NavLink>
      </div>
      {showRewardsOrders && (
        <div className="nav-group">
          <GroupHeader
            pathPrefixes={['/account/rewards']}
            className="nav-link top-level-nav-item"
            activeClassName="activeAccountTab"
          >
            <RewardsOrderHistoryIcon />
            Rewards History
          </GroupHeader>
          {isCAPractitioner &&
            rewardsSources.map(({ key, name, slug }) => {
              return (
                <Link
                  to={`/account/rewards/orders/${slug}`}
                  getProps={getPropsForNestedNavLink}
                  key={key}
                >
                  {name}
                </Link>
              )
            })}
          {isUSPractitioner &&
            rewardsHistorySources.map(({ key, name, slug }) => {
              return (
                <Link
                  to={`/account/rewards/invoices/${slug}`}
                  getProps={getPropsForNestedNavLink}
                  key={key}
                >
                  {name}
                </Link>
              )
            })}
        </div>
      )}
      {/* TODO: Uncomment the link below when the page is complete. */}
      {/*
          <NavLink
            closeMenu={closeMenu}
            to="/account/rebate-history"
            className="nav-link nested-nav-link"
            activeClassName="activeAccountTab"
          >
            Rebate History
          </Link>
        */}
      <div className="nav-group">
        <GroupHeader
          pathPrefixes={['/account/addresses', '/account/credit-cards', '/account/transactions']}
          className="nav-link top-level-nav-item"
          activeClassName="activeAccountTab"
        >
          <BillingShippingIcon />
          Billing/Shipping
        </GroupHeader>
        <NavLink closeMenu={closeMenu} to="/account/addresses" getProps={getPropsForNestedNavLink}>
          Address Book
        </NavLink>
        <NavLink
          closeMenu={closeMenu}
          to="/account/credit-cards"
          getProps={getPropsForNestedNavLink}
        >
          Credit Cards
        </NavLink>
        <NavLink
          closeMenu={closeMenu}
          to="/account/transactions"
          getProps={getPropsForNestedNavLink}
        >
          Invoice History
        </NavLink>
      </div>
      {isPractitioner && showPrivateLabelCustomPackets && (
        <div className="nav-group">
          <GroupHeader
            pathPrefixes={['/account/packets']}
            className="nav-link top-level-nav-item"
            activeClassName="activeAccountTab"
          >
            <PacketsIcon />
            Packets
          </GroupHeader>
          <NavLink closeMenu={closeMenu} to="/account/packets" getProps={getPropsForNestedNavLink}>
            All Packets
          </NavLink>
        </div>
      )}
      <div className="nav-group">
        <GroupHeader
          pathPrefixes={['/account/settings']}
          className="nav-link top-level-nav-item"
          activeClassName="activeAccountTab"
        >
          <AccountSettingsIcon />
          Account Settings
        </GroupHeader>
        {isPractitioner && (
          <>
            {showDispensingOptions && (
              <NavLink
                closeMenu={closeMenu}
                to="/account/settings/dispensing-options"
                getProps={getPropsForNestedNavLink}
              >
                Dispensing Options
              </NavLink>
            )}
            {showRebateProgram && (
              <NavLink
                closeMenu={closeMenu}
                to="/account/settings/rewards-program"
                getProps={getPropsForNestedNavLink}
              >
                Rewards Program
              </NavLink>
            )}
          </>
        )}
        {(isPractitioner || legalAgreements === 'PL') && (
          <>
            {eftAndTaxLink}
            <NavLink
              closeMenu={closeMenu}
              to="/account/settings/agreements"
              getProps={getPropsForNestedNavLink}
            >
              Agreements
            </NavLink>
          </>
        )}
        <NavLink
          closeMenu={closeMenu}
          to="/account/settings/password"
          getProps={getPropsForNestedNavLink}
        >
          Email & Password
        </NavLink>
      </div>
    </nav>
  )
}

const StyledNav = styled(Nav)(({ theme }) => ({
  // Specificity of tag plus class is needed to override some Bootstrap defaults:
  '&.nav': {
    flex: '0 0 var(--nav-width)',
    flexDirection: 'column',
    padding: '40px 0 40px 40px',
    boxSizing: 'border-box',
    '&:not(.mobile-nav)': {
      display: 'none',
    },
  },
  '.nav-group:not(:last-of-type)': {
    marginBottom: '29px',
  },
  '.nav-link': {
    display: 'block',
    fontFamily: theme.fontFamilies.robotoCondensed,
    fontSize: '14px',
    lineHeight: '16px',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    '&, &:visited': {
      color: theme.colors.grayscale.black,
      textDecoration: 'none',
    },
  },
  '.nested-nav-link': {
    paddingBottom: '0',
    paddingRight: '0',
    paddingTop: '20px',
    paddingLeft: '35px',
    '&:first-of-type': {
      paddingTop: '29px',
    },
    '&, &:visited': {
      color: theme.colors.text.primary,
    },
  },
  '.activeAccountTab': {
    fontWeight: 'bold',
    svg: { color: theme.colors.brand.orange },
  },
  '.top-level-nav-item': {
    alignItems: 'center',
    display: 'flex',
    height: '20px',
    lineHeight: '20px',
    padding: '0',
  },
  '@media screen and (min-width: 768px)': {
    '&.nav': {
      display: 'none',
      '&:not(.mobile-nav)': {
        display: 'flex',
      },
      '&.account-menu-nav': {
        display: 'flex',
      },
    },
  },
  '@media print': {
    display: 'none',
  },
}))

export default StyledNav
